/**
 * Largeur minimale de mise en page de l'app.
 *
 * L'app est conçue pour 375 px (règle mobile-first, CLAUDE.md § 2). Certains
 * Android offrent MOINS de largeur : Chrome applique le zoom du téléphone
 * (taille de police / d'affichage du système) à toute la page — un Honor
 * réglé « grand » donne ~320 px utiles : police énorme et contenu qui
 * déborde à droite. Dans ce cas on déclare une page de 375 px et le
 * navigateur la RÉDUIT pour tenir dans l'écran : mêmes proportions que sur
 * un iPhone. Un écran d'au moins 375 px n'est jamais touché.
 */

export const MIN_LAYOUT_WIDTH = 375;

/** Balise viewport d'origine (index.html) : aucun ajustement. */
export const BASE_VIEWPORT =
  'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, viewport-fit=cover';

/**
 * Contenu de la balise viewport pour une largeur d'écran donnée (px CSS,
 * mesurée avec la balise d'origine). Pur.
 */
export function fittedViewportContent(deviceWidth: number, minWidth = MIN_LAYOUT_WIDTH): string {
  // Mesure absurde (0, NaN) ou écran assez large : on ne touche à rien.
  if (!Number.isFinite(deviceWidth) || deviceWidth <= 0 || deviceWidth >= minWidth - 0.5) {
    return BASE_VIEWPORT;
  }
  const scale = Math.floor((deviceWidth / minWidth) * 1000) / 1000;
  return `width=${minWidth}, initial-scale=${scale}, minimum-scale=${scale}, maximum-scale=${scale}, viewport-fit=cover`;
}

function viewportMeta(): HTMLMetaElement | null {
  return document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
}

/** Mesure la largeur réelle (balise d'origine) puis applique l'ajustement. */
function refit(meta: HTMLMetaElement): void {
  meta.setAttribute('content', BASE_VIEWPORT);
  // Laisse le navigateur appliquer la balise d'origine avant de mesurer.
  requestAnimationFrame(() => {
    meta.setAttribute('content', fittedViewportContent(window.innerWidth));
  });
}

/**
 * À appeler au démarrage (avant le premier rendu). Réajuste seulement quand
 * l'ORIENTATION change — jamais à l'ouverture du clavier (qui ne change que
 * la hauteur) : sinon l'écran clignoterait à chaque saisie.
 */
export function initViewportFit(): void {
  const meta = viewportMeta();
  if (!meta) return;
  meta.setAttribute('content', fittedViewportContent(window.innerWidth));

  const portrait = window.matchMedia?.('(orientation: portrait)');
  portrait?.addEventListener?.('change', () => refit(meta));
}
